import { Button, Flex, Form, Space, Spin } from "antd"
import PropTypes from "prop-types"
import { colors, spacings } from "themes/variables"
import { Text } from "ui/Typography"
import { useDispatch, useSelector } from "react-redux"
import { useEffect, useState } from "react"
import { adjustBookingPrice, getClientBookingDetails } from "store/bookings"
import { toaster } from "components/common/Toast"
import { useTranslation } from "react-i18next"
import PriceAndReasonFields from "../PriceAndReasonFields"
import { LoadingOutlined } from "@ant-design/icons"
import { numberToCurrency } from "helpers/string"

const CashPaymentForm = ({ currentPrice, bookingUUID, onClose, onSubmitClose, onUnsavedChangesChange }) => {
  const [form] = Form.useForm()
  const [hasErrors, setHasErrors] = useState(true)
  const dispatch = useDispatch()
  const { loading } = useSelector((state) => state.bookings.adjustBookingPrice)
  const { t } = useTranslation()

  const amountToPay = Form.useWatch("amount", form)
  const reason = Form.useWatch("reason", form)
  const hasUnsavedChanges = Boolean(amountToPay) || Boolean(reason)

  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges)
  }, [hasUnsavedChanges, onUnsavedChangesChange])

  const handleSubmit = (data) => {
    dispatch(adjustBookingPrice(bookingUUID, { new_price: Number(data?.amount), reason: data?.reason, stripe: false }))
      .then((response) => {
        if (response?.success) {
          toaster.success(response?.message)
          form.resetFields()
          onUnsavedChangesChange?.(false)
          if (onSubmitClose) {
            onSubmitClose()
            return
          }
          onClose?.()
          dispatch(getClientBookingDetails(bookingUUID))
        }
      })
      .catch((error) => {
        if (error?.data?.is_amount_lower) {
          setHasErrors(true)
          form.setFields([
            {
              name: "amount",
              errors: [error?.data?.message]
            }
          ])
        }
      })
  }

  const handleFieldsChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0)
    const allTouched = form.isFieldsTouched(true)
    setHasErrors(hasErrors || !allTouched)
  }

  return (
    <Spin spinning={loading} indicator={<LoadingOutlined spin />} size="large">
      <Space vertical size={24} className="w-full mt-3xl" direction="vertical">
        <Flex gap={spacings.S} style={{ backgroundColor: colors.surface1 }} className="py-xs px-s">
          <Text color={colors.textSecondaryDark}>{t("client.client_booking.current_price")}: </Text>
          <Text color={colors.textPrimary}>{numberToCurrency(currentPrice)}</Text>
        </Flex>
        <Form layout="vertical" form={form} onFinish={handleSubmit} onFieldsChange={handleFieldsChange}>
          <PriceAndReasonFields />
          <Flex gap={spacings.S} className="mt-3xl">
            <Button className="py-l px-xl" color="primary" variant="outlined" onClick={onClose}>
              {t("global.cancel")}
            </Button>
            <Button type="primary" className="py-l px-xl" htmlType="submit" disabled={hasErrors}>
              {t("global.submit")}
            </Button>
          </Flex>
        </Form>
      </Space>
    </Spin>
  )
}

CashPaymentForm.propTypes = {
  currentPrice: PropTypes.number,
  bookingUUID: PropTypes.string,
  onClose: PropTypes.func,
  onSubmitClose: PropTypes.func,
  onUnsavedChangesChange: PropTypes.func
}

export default CashPaymentForm
